<!--@format-->
<template>
    <div class="payment-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ isLoading ? 'Loading...' : currentStep.title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else class="widget-container">
                    <make-payment-step
                        v-if="currentStep === steps.makePaymentStep"
                        ref="contentStep"
                        :payment-data="paymentDetails"
                        :invoice-table-details="invoiceTableDetails"
                        @next-step="goToStep('successStep')"
                    />

                    <success-step
                        v-if="currentStep === steps.successStep"
                        ref="usersStep"
                        :payment-data="paymentDetails"
                        :invoice-table-details="invoiceTableDetails"
                        @next-step="closeWidget"
                    />
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button
                        v-if="currentStep.firstButton"
                        class="cancel-btn"
                        @click="closeWidget"
                    >
                        Cancel
                    </app-button>
                </div>
                <app-button
                    :class="[
                        {
                            'save-btn': !currentStep.finalStep,
                            'save-final-btn': currentStep.finalStep,
                        },
                    ]"
                    :loading="loadingData.value"
                    @click="currentStep.actionButton"
                >
                    {{ currentStep.secondButton }}
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const SuccessStep = () => import('@/components/sliding-widgets/payment-widget/success-step');
const MakePaymentStep = () =>
    import('@/components/sliding-widgets/payment-widget/make-payment-step');
import EventBus from '@/scripts/event-bus.js';
import { PaymentMethodsService } from '@/scripts/services/payment-methods/payment-methods-service';
import { InvoicesService } from '@/scripts/services/invoices/invoices-service.js';
import { LogLevel } from '@/scripts/logger';

export default {
    name: 'payment-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'success-step': SuccessStep,
        'make-payment-step': MakePaymentStep,
    },

    provide() {
        return {
            loadingData: this.loadingData,
            clientId: this.widget.clientId,
            agencyId: this.widget.agencyId,
            accountId: this.widget.accountId,
        };
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            clientId: this.widget.clientId,
            agencyId: this.widget.agencyId,
            currentStep: null,
            isLoading: false,
            loadingData: { value: false },

            invoiceTableDetails: [],

            paymentDetails: {
                paymentMethods: [],
                payMethod: {},
                agreement: false,
                companyName: '',
                invoiceNumber: '',
                amount: '',
                invoiceId: '',
            },

            steps: {
                makePaymentStep: {
                    title: 'Make a Payment',
                    firstButton: 'Cancel',
                    secondButton: 'Make Payment',
                    finalStep: false,
                    loading: false,
                    actionButton: () => {
                        this.$refs.contentStep.next();
                    },
                },

                successStep: {
                    title: 'Success',
                    firstButton: null,
                    secondButton: 'Next',
                    finalStep: true,
                    loading: false,
                    actionButton: () => {
                        this.$refs.usersStep.next();
                    },
                },
            },
        };
    },

    computed: {
        hasAgencyBillingPaymentPermission() {
            return (
                this.$store.getters.isBillingPaymentUser ||
                this.$store.getters.isOrganizationUser ||
                this.$store.getters.isMasterAgencyUser ||
                this.$store.getters.isAgencyAdmin
            );
        },

        isAgencyContext() {
            return this.$store.state.context.agency.id;
        },

        userConfig() {
            if (this.clientId) {
                return {
                    userType: 'client',
                    id: this.clientId,
                    invoiceMode: 'partner',
                    invoiceTableDetails: (data) => this.getInvoiceDetails(data, 'partner'),
                };
            } else if (this.agencyId) {
                return {
                    userType: 'agency',
                    id: this.agencyId,
                    invoiceMode: 'agency',
                    invoiceTableDetails: (data) => this.getInvoiceDetails(data, 'agency'),
                };
            } else {
                return {
                    userType: 'masterAgency',
                    id: this.agencyId,
                    invoiceMode: 'masterAgency',
                    invoiceTableDetails: {},
                };
            }
        },
    },

    created() {
        const hasPermissions = this.validateRoles();
        if (!hasPermissions) {
            return;
        }

        this.isLoading = true;
        this.goToStep('makePaymentStep');
        const params = 'include=["paymentMethod"]';
        InvoicesService.getByNumber(this.widget.invoiceId, params)
            .then(({ data: { data } }) => {
                this.invoiceTableDetails = this.userConfig.invoiceTableDetails(data);
                this.paymentDetails = {
                    ...this.paymentDetails,
                    companyName: data.account.Name,
                    invoiceNumber: data.invoice.InvoiceNumber,
                    amount: data.invoice.Amount,
                    invoiceId: data.invoice.Id,
                };
            })
            .then(this.getCards)
            .catch((error) => {
                this.$helpers.showFailNotification(
                    'An error occurred while fetching your information.',
                    { error },
                );
            })
            .finally(() => {
                this.isLoading = false;
            });
    },

    methods: {
        validateRoles() {
            if (this.isAgencyContext && !this.hasAgencyBillingPaymentPermission) {
                this.closeWidget();
                const errorMessage =
                    'You need additional permissions in order to perform this action.';
                this.$helpers.showFailNotification(errorMessage, { level: LogLevel.INFO });
                return false;
            }
            return true;
        },

        getInvoiceDetails(data, role) {
            const startBillingPeriod = data.invoice.PeriodStartDate
                ? this.$moment.utc(data.invoice.PeriodStartDate).local()
                : null;

            const endBillingPeriod = data.invoice.PeriodEndDate
                ? this.$moment.utc(data.invoice.PeriodEndDate).local()
                : null;

            const baseFee = data.invoice.PromoBasePrice || 0;

            const users =
                (data.invoice.UsersNumber - data.invoice.PromoFreeUsers) *
                data.invoice.PromoUserPrice;

            const companySection = [
                {
                    title: 'Company Name',
                    content: data.account.Name,
                },
                {
                    title: 'Invoice Number',
                    content: data.invoice.InvoiceNumber,
                },
                {
                    title: 'Item',
                    content: data.invoice.PromoName ? data.invoice.PromoName : '',
                },
                {
                    title: 'Billing Period',
                    content: `${this.formatDate(startBillingPeriod)} - ${this.formatDate(endBillingPeriod)}`,
                    contentClass: 'uppercase',
                },
            ];

            const feeSection = [
                {
                    title: 'Sales Tax',
                    content: data.invoice.Tax,
                    type: 'currency',
                },
            ];

            if (role === 'partner') {
                feeSection.unshift(
                    ...[
                        {
                            title: 'Base Fee',
                            content: data.invoice.PromoBasePrice || 0,
                            type: 'currency',
                        },
                        {
                            title: 'Users',
                            additionalTitleInfo: `(${data.invoice.UsersNumber - data.invoice.PromoFreeUsers})`,
                            content: users,
                            type: 'currency',
                        },
                    ],
                );
            } else {
                feeSection.unshift({
                    title: 'Partner Visibility Fee',
                    additionalTitleInfo: `(${this.formatCurrency(baseFee)} / partner)`,
                    content: baseFee * data.invoice.UsersNumber,
                    type: 'currency',
                });
            }

            const amountSection = [
                {
                    title: 'Amount Due',
                    content: data.invoice.Amount,
                    rowClass: 'extra-bold',
                    type: 'currency',
                },
            ];
            return [companySection, feeSection, amountSection];
        },

        getCards() {
            return PaymentMethodsService.getAllVerified(
                this.userConfig.id,
                this.userConfig.userType,
            ).then(({ data }) => {
                this.paymentDetails.paymentMethods = data.data
                    .map(this.mapItems)
                    .sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
                this.paymentDetails.payMethod = this.paymentDetails.paymentMethods[0];
            });
        },

        convertToFourDigitYear(twoDigitYear) {
            if (!twoDigitYear || isNaN(twoDigitYear)) {
                return null;
            }
            const currentYear = new Date().getFullYear();
            const currentCentury = Math.floor(currentYear / 100) * 100;
            return currentCentury + parseInt(twoDigitYear, 10);
        },

        mapItems(item) {
            if (item.PaymentType === 'card') {
                const month =
                    item.CardExpirationMonth && !isNaN(item.CardExpirationMonth)
                        ? parseInt(item.CardExpirationMonth, 10) - 1
                        : null;
                const year = this.convertToFourDigitYear(item.CardExpirationYear);
                let expirationDate = '';
                if (month !== null && year !== null) {
                    expirationDate = this.$moment().year(year).month(month);
                }
                return {
                    type: item.Type,
                    cardType: item.CardType,
                    lastFourNumbers: item.CardLastFour,
                    isDefault: item.IsDefault,
                    key: `${item.CardLastFour}-${item.CardType}`,
                    id: item.Id,
                    imageCard: this.$helpers.setCardTypeData(item.CardType),
                    cardName: item.NickName,
                    expirationDate,
                };
            }
            return {
                accountType: item.BankAccountHolderType,
                lastFourNumbers: item.BankAccountLastFour,
                type: item.Type,
                id: item.Id,
                key: `${item.BankAccountLastFour}-${item.BankAccountHolderType}`,
                accountName: item.NickName,
                expirationDate: '',
                isDefault: item.IsDefault,
            };
        },

        goToStep(step) {
            this.currentStep = this.steps[step];
        },

        formatCurrency(amount) {
            const currency = parseFloat(amount || 0);
            return '$' + currency.toFixed(2);
        },

        formatDate(date) {
            if (date) {
                return date.local().format('MMM D, YYYY');
            }
            return '';
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.payment-widget {
    color: #382153;
}

.payment-widget .save-final-btn {
    width: 128px;
    height: 40px;
}

.payment-widget .cancel-btn {
    background: transparent;
    border: none;
    font: normal normal medium 14px/24px Roboto;
    color: #877d88;
    min-width: 128px;
    min-height: 40px;
}

.payment-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.payment-widget .save-btn {
    background: transparent linear-gradient(0deg, #28da34 0%, #13e9e7 100%) 0% 0% no-repeat
        padding-box;
    border-radius: 20px;
    width: 205px;
    height: 40px;
}

.payment-widget .save-btn:hover {
    background: transparent linear-gradient(0deg, #28da34 0%, #13e9e7 100%) 0% 0% no-repeat
        padding-box;
    border-radius: 20px;
}
</style>
